import React, { useCallback } from 'react'
import styled, { ThemeProvider, keyframes } from 'styled-components'
import moment from 'moment'
import get from 'lodash/get'
import first from 'lodash/first'
import find from 'lodash/find'
import toLower from 'lodash/toLower'
import capitalize from 'lodash/capitalize'

import themeColors from '@layers-frontend/commons/styles/themeColors'
import { CALENDAR_LEGENDS } from '@layers-frontend/commons/constants'

import withTranslator from '../HOCComponents/withTranslator'
import { StyledTooltip } from '../UIComponents/StyledTooltip'
import { UIStyles } from '../../theme'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 1rem;
  border-bottom: 1px solid ${themeColors.mintBorder};
  border-top: 1px solid ${themeColors.mintBorder};
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const InfoGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  &:not(:last-child) {
    margin-right: 16px;
  }
  cursor: pointer;

  &:hover {
    .value {
      color: ${themeColors.mintColor};
    }
  }
`

const Label = styled.span`
  color: ${themeColors.grayColor};
  font-size: 11px;
`

const Value = styled.span`
  color: ${themeColors.whiteColor};
  font-size: 12px;
  font-weight: 500;
  transition: color 0.3s ease;

  &:hover {
    color: ${themeColors.mintColor};
  }

  &.value {
    transition: all 0.5s ease;
  }
`

const ColorDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.color};
  margin-right: 2px;
  display: inline-block;
  vertical-align: middle;
`

const fadeInOut = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
`

const RightIconWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  color: ${props => props.color || UIStyles.mintColor};
  transform: ${props => (props.rotated ? 'scaleY(-1)' : 'none')};
  transition: transform 0.3s ease;
  animation: ${props => (props.rotated ? fadeInOut : 'none')} 0.3s ease;
`

const StyledIcon = styled.i`
  font-size: ${props => props.size || '1rem'};
  color: ${props => props.color || 'inherit'};
  cursor: ${props => (props.pointer ? 'pointer' : 'default')};
  transition: color 0.3s ease;

  &:hover {
    color: ${themeColors.mintColor};
  }
`

const TabsHeader = ({
  selectedDate,
  selectedSource,
  activeLayers,
  allTabs,
  setSelectedFieldTab,
  expandSelectedFieldsAside,
  asideExpanded,
  toggleExpandSelectedFieldAside,
  seasonLayers,
  seasonLayersBaseZIndex,
  groupDeliverablesBaseZIndex,
  t
}) => {
  const activeLayer = first(activeLayers)
  const activeSeasonLayer = find(seasonLayers, layer => layer.visible)
  const isStaticLayerVisible = activeSeasonLayer && (!activeLayer || seasonLayersBaseZIndex > groupDeliverablesBaseZIndex)
  const layerName = isStaticLayerVisible ? t(get(activeSeasonLayer, 'type.name', '-')) : t(get(activeLayer, 'type.name', '-'))
  const sourceName = isStaticLayerVisible ? capitalize(t('static layers')) : activeLayer ? get(CALENDAR_LEGENDS, [selectedSource, 'name'], '-') : '-'
  const sourceColor = isStaticLayerVisible ? themeColors.whiteColor : get(CALENDAR_LEGENDS, [selectedSource, 'color'])

  const formattedDate = selectedDate ? moment(selectedDate, 'YYYY-MM-DD').format('DD MMM YYYY') : '-'

  const handleCalendarClick = useCallback(() => {
    const calendarTab = find(allTabs, { name: 'calendar' })
    if (calendarTab) {
      const tabIndex = allTabs.indexOf(calendarTab)
      setSelectedFieldTab(tabIndex)
      expandSelectedFieldsAside()
    }
  }, [allTabs, setSelectedFieldTab, expandSelectedFieldsAside])

  const handleLayersClick = useCallback(() => {
    const layersTab = find(allTabs, { name: 'layers' })
    if (layersTab) {
      const tabIndex = allTabs.indexOf(layersTab)
      setSelectedFieldTab(tabIndex)
      expandSelectedFieldsAside()
    }
  }, [allTabs, setSelectedFieldTab, expandSelectedFieldsAside])

  return (
    <ThemeProvider theme={{ colors: themeColors }}>
      <Wrapper>
        <TopRow>
          <InfoGroup onClick={handleLayersClick}>
            <Label>{t('source')}:</Label>
            {!isStaticLayerVisible && activeLayer && <ColorDot color={sourceColor} />}
            <Value className="value">{t(sourceName)}</Value>
          </InfoGroup>
          {!isStaticLayerVisible && (
            <InfoGroup onClick={handleCalendarClick}>
              <Label>{t('date')}:</Label>
              <Value className="value">{formattedDate}</Value>
            </InfoGroup>
          )}
        </TopRow>
        <BottomRow>
          <InfoGroup onClick={handleLayersClick}>
            <Label>{t('top layer')}:</Label>
            <Value className="value">{t(toLower(layerName))}</Value>
          </InfoGroup>
          <RightIconWrapper color={UIStyles.mintColor} rotated={!asideExpanded}>
            <StyledTooltip title={capitalize(t(asideExpanded ? 'hide' : 'show details'))} placement="bottom">
              <StyledIcon size="2rem" className="icon-arrow" onClick={() => toggleExpandSelectedFieldAside(!asideExpanded)} />
            </StyledTooltip>
          </RightIconWrapper>
        </BottomRow>
      </Wrapper>
    </ThemeProvider>
  )
}

export default withTranslator(TabsHeader)
